import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Observable} from 'rxjs';
import {Cluster} from '../../../services/api/clusters/cluster.model';
import {selectActiveClusters as getActiveClusters} from '../../../store/clusters';
import {Store} from '@ngrx/store';
import {map} from 'rxjs/operators';
import {DvrRecord} from '../../../services/api/dvr/dvr.model';
import {getRecordsForClipSource} from '../../../store/dvr/selectors/clip-sources-records.selectors';

export type EntityType = 'stream' | 'destination' | 'grid' | 'dvr' | 'clip' | 'production';

export interface EntityConfiguration {
  name: string;
  url?: string | undefined;
  btnTitle: string;
  clusterRequired: boolean;
  requiredResources: RequiredResourceType[];
}

type EntityConfigurations = {
  [key in EntityType]: EntityConfiguration;
};

export const configurations: EntityConfigurations = {
  stream: {
    name: 'Stream',
    url: '/streams/new',
    btnTitle: 'Add New Stream',
    clusterRequired: true,
    requiredResources: ['active-clusters']
  },
  destination: {
    name: 'Destination',
    url: '/destinations/new',
    btnTitle: 'Add New Destination',
    clusterRequired: true,
    requiredResources: ['active-clusters']
  },
  grid: {
    name: 'Multiview',
    url: '/multiview/new',
    btnTitle: 'Add New Multiview',
    clusterRequired: true,
    requiredResources: ['active-clusters']
  },
  dvr: {
    name: 'DVR',
    btnTitle: 'Add New Recording',
    clusterRequired: true,
    requiredResources: ['active-clusters']
  },
  clip: {
    name: 'Clip',
    url: '/clips/new',
    btnTitle: 'Create A Clip',
    clusterRequired: true,
    requiredResources: ['active-clusters', 'clip-source-records']
  },
  production: {
    name: 'Production Instance',
    url: '/production/new',
    btnTitle: 'Add New Instance',
    clusterRequired: false,
    requiredResources: []
  }
};

export type RequiredResourceType = 'active-clusters' | 'clip-source-records';

@Component({
  selector: 'app-entity-manage-button-container',
  template: `
    <app-entity-manage-button
      [configuration]="configuration"
      [isActiveClustersAvailable]="isActiveClustersAvailable$ | async"
      [isClipSourceRecordsAvailable]="isClipSourceRecordsAvailable$ | async"
      (clickEmit)="clickEmit.emit()"
    ></app-entity-manage-button>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EntityManageButtonContainerComponent implements OnInit {
  @Input() entity: EntityType | undefined = undefined;
  @Output() clickEmit: EventEmitter<void> = new EventEmitter();
  public isActiveClustersAvailable$: Observable<boolean> | undefined = undefined;
  public isClipSourceRecordsAvailable$: Observable<boolean> | undefined = undefined;
  public configuration: EntityConfiguration | undefined = undefined;

  constructor(
    private readonly store: Store
  ) {
  }

  public ngOnInit(): void {
    this.configuration = configurations[this.entity];

    const isActiveClustersRequired: boolean = this.configuration.requiredResources.includes('active-clusters');
    if (isActiveClustersRequired) {
      this.isActiveClustersAvailable$ = this.isActiveClustersAvailable();
    }

    const isClipSourceRecordsRequired: boolean = this.configuration.requiredResources.includes('clip-source-records');
    if (isClipSourceRecordsRequired) {
      this.isClipSourceRecordsAvailable$ = this.isClipSourceRecordsAvailable();
    }
  }

  private isActiveClustersAvailable(): Observable<boolean> {
    return this.store
      .select(getActiveClusters)
      .pipe(
        map((clusters: Cluster[]): boolean => clusters.length > 0)
      );
  }

  private isClipSourceRecordsAvailable(): Observable<boolean> {
    return this.store
      .select(getRecordsForClipSource)
      .pipe(
        map((records: DvrRecord[]): boolean => records.length > 0)
      );
  }
}
